import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Typography, Box, Paper, CircularProgress, Grid, Card, CardContent, Divider, Chip, Button } from '@mui/material';
import { format } from 'date-fns';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { orderService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const formatCurrency = (v) => `₹${Number(v||0).toFixed(2)}`;

const statusColor = (status) => {
  const s = (status||'').toLowerCase();
  if(['confirmed','processing','shipped','delivered'].includes(s)) return 'success';
  if(['pending','pending_payment'].includes(s)) return 'warning';
  if(['payment_failed','cancelled','failed'].includes(s)) return 'error';
  return 'default';
};

const OrderDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(()=>{ fetchOrder(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function fetchOrder(){
    try { setLoading(true); setError('');
      const res = await orderService.getOrder(id);
      setOrder(res.data.order);
    } catch(e){ console.error('Order detail fetch error', e); setError(e.response?.data?.error||'Failed to load order'); }
    finally { setLoading(false); }
  }

  const formatDate = (value) => {
    if (!value) return 'N/A';
    try {
      let date;
      if (value?.toDate && typeof value.toDate === 'function') {
        date = value.toDate();
      } else if (value && typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
        date = new Date(value.seconds * 1000);
      } else if (typeof value === 'number') {
        date = value < 1e12 ? new Date(value * 1000) : new Date(value);
      } else if (typeof value === 'string') {
        const firestoreHumanPattern = /at\s/;
        if (firestoreHumanPattern.test(value) && !/T\d{2}:\d{2}/.test(value)) {
          const cleaned = value.replace(' at', '').replace('UTC', 'GMT');
          date = new Date(cleaned);
        } else {
          date = new Date(value);
        }
      } else if (value instanceof Date) {
        date = value;
      }
      if (!date || isNaN(date.getTime())) return 'Invalid Date';
      return format(date, 'PPp');
    } catch (e) {
      console.error('Failed to format date:', value, e);
      return 'Invalid Date';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py:6 }}>
      <Box mb={3} display="flex" alignItems="center" gap={1}>
        <Button component={Link} to="/orders" startIcon={<ArrowBackIcon/>} size="small" variant="outlined">Back</Button>
        <Typography variant="h4" fontWeight={600}>Order Details</Typography>
      </Box>
      <Paper sx={{ p:3 }}>
        {loading && (
          <Box textAlign="center" py={6}>
            <CircularProgress />
            <Typography variant="body2" mt={2}>Loading order...</Typography>
          </Box>
        )}
        {!loading && error && (
          <Box textAlign="center" py={4}>
            <Typography color="error" gutterBottom>{error}</Typography>
            <Button variant="outlined" onClick={fetchOrder}>Retry</Button>
          </Box>
        )}
        {!loading && order && (
          <Box>
            <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>#{order.orderNumber || order.id}</Typography>
              <Chip label={order.status || 'pending'} color={statusColor(order.status)} size="small" />
              <Chip label={(order.paymentStatus||'').toUpperCase()} variant="outlined" size="small" />
              <Typography variant="caption" color="text.secondary">Placed {formatDate(order.createdAt)}</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Items</Typography>
                {order.items && order.items.length>0 ? order.items.map((it,i)=>(
                  <Card key={i} variant="outlined" sx={{ mb:1 }}>
                    <CardContent sx={{ py:1.5, '&:last-child':{ pb:1.5 } }}>
                      <Box display="flex" gap={2}>
                        {it.image && <Box component="img" src={it.image} alt={it.name} sx={{ width:64, height:64, objectFit:'cover', borderRadius:1, border:'1px solid #eee' }} />}
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600}>{it.name}</Typography>
                          {it.customizations?.text && (
                            <Typography variant="caption" color="text.secondary">Text: {it.customizations.text}</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">Qty: {it.quantity}</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(it.price * (it.quantity||1))}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )): <Typography variant="body2" color="text.secondary">No items.</Typography>}
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p:2 }}>
                  <Typography variant="subtitle2" gutterBottom>Price Details</Typography>
                  <Box display="flex" justifyContent="space-between" mb={0.5}><Typography variant="caption">Subtotal</Typography><Typography variant="caption">{formatCurrency(order.pricing?.subtotal)}</Typography></Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}><Typography variant="caption">Shipping</Typography><Typography variant="caption">{formatCurrency(order.pricing?.shipping)}</Typography></Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}><Typography variant="caption">Tax (incl.)</Typography><Typography variant="caption">{formatCurrency(order.pricing?.tax)}</Typography></Box>
                  {order.paymentMethod === 'cod' && order.pricing?.codCharge > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="warning.main">Cash Handling Charges</Typography>
                      <Typography variant="caption" color="warning.main">{formatCurrency(order.pricing.codCharge)}</Typography>
                    </Box>
                  )}
                  {order.pricing?.discount>0 && <Box display="flex" justifyContent="space-between" mb={0.5}><Typography variant="caption">Discount</Typography><Typography variant="caption">- {formatCurrency(order.pricing?.discount)}</Typography></Box>}
                  <Divider sx={{ my:1 }}/>
                  <Box display="flex" justifyContent="space-between" mb={0.5}><Typography variant="body2" fontWeight={600}>Total</Typography><Typography variant="body2" fontWeight={600}>{formatCurrency(order.pricing?.total)}</Typography></Box>
                </Paper>
                <Paper variant="outlined" sx={{ p:2, mt:2 }}>
                  <Typography variant="subtitle2" gutterBottom>Delivery Address</Typography>
                  {order.shippingAddress ? (
                    <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight:1.4 }}>
                      {order.shippingAddress.name || user?.displayName}<br/>
                      {order.shippingAddress.addressLine1}{order.shippingAddress.addressLine2?`, ${order.shippingAddress.addressLine2}`:''}<br/>
                      {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}<br/>
                      Ph: {order.shippingAddress.phoneNumber}
                    </Typography>
                  ) : <Typography variant="caption" color="text.secondary">N/A</Typography>}
                </Paper>
              </Grid>
            </Grid>
            <Divider sx={{ my:3 }} />
            <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              <Button variant="contained" component={Link} to="/orders" startIcon={<ShoppingBagIcon/>}>My Orders</Button>
              <Button variant="outlined" component={Link} to="/products">Continue Shopping</Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default OrderDetail;
